<script setup>
import { ref } from 'vue';

const markets = ref([
  { flag: '🇺🇸', name: 'US', exchanges: 'NYSE/NASDAQ' },
  { flag: '🇬🇧', name: 'UK', exchanges: 'LSE' },
  { flag: '🇩🇪', name: 'Germany', exchanges: 'XETRA' },
  { flag: '🇯🇵', name: 'Japan', exchanges: 'TSE' },
  { flag: '🇸🇬', name: 'Singapore', exchanges: 'SGX' },
  { flag: '🇭🇰', name: 'Hong Kong', exchanges: 'HKEX' }
]);

const plans = ref([
  { name: 'Global Classic', fee: '₹0', features: ['Basic access', 'Manual trading', 'Standard support'] },
  { name: 'Global Select', fee: '₹999', features: ['Enhanced features', 'Research reports', 'Priority support'] },
  { name: 'Global Advantage', fee: '₹9,999', features: ['Premium features', 'Managed portfolios', 'Dedicated RM'] }
]);
</script>

<template>
  <div class="direct-equity">
    <h2 class="main-title">Direct Equity Investment via ICICI Direct Global</h2>
    <p class="main-intro">
      <strong>Broker-Referral Architecture:</strong> ICICI Securities (Introducing Broker) + Interactive Brokers LLC/IBKR (Executing & Clearing Broker). Direct legal ownership in 6 global markets.
    </p>

    <!-- Platform Info -->
    <div class="platform-section">
      <div class="platform-card">
        <h4>🏦 Platform Architecture</h4>
        <div class="architecture-info">
          <div class="arch-item">
            <strong>Introducing Broker:</strong> ICICI Securities (India)
          </div>
          <div class="arch-item">
            <strong>Executing Broker:</strong> Interactive Brokers LLC (US)
          </div>
          <div class="arch-item">
            <strong>Regulation:</strong> FINRA & SEC (US)
          </div>
          <div class="arch-item">
            <strong>Custodian:</strong> Interactive Brokers LLC
          </div>
          <div class="arch-item">
            <strong>Protection:</strong> SIPC up to $500,000 (incl. $250,000 cash)
          </div>
        </div>
      </div>

      <div class="platform-card">
        <h4>🌍 Global Market Access</h4>
        <div class="markets-grid">
          <div v-for="market in markets" :key="market.name" class="market-badge">
            <span class="market-flag">{{ market.flag }}</span>
            <div class="market-info">
              <strong>{{ market.name }}</strong>
              <span class="market-exchange">{{ market.exchanges }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Process Flow -->
    <div class="process-section">
      <h3 class="section-header">📋 Investment Process Flow</h3>
      <div class="process-grid">
        <!-- Phase A -->
        <div class="process-card purple">
          <div class="phase-label">PHASE A</div>
          <h4>🔐 Account Opening & Verification</h4>
          <div class="step-content">
            <p><strong>1. Domestic Login:</strong> Log in to existing ICICI Direct account (mandatory)</p>
            <p><strong>2. Plan Subscription:</strong></p>
            <div class="plans-mini">
              <div v-for="plan in plans" :key="plan.name" class="plan-mini">
                <strong>{{ plan.name }}:</strong> {{ plan.fee }}
              </div>
            </div>
            <p><strong>3. Document Upload:</strong> PAN card + Address proof (digital only)</p>
            <p><strong>4. Verification:</strong> Account opened with IBKR (US-regulated)</p>
            <p class="highlight">📌 Trading ID: US brokerage account (e.g., U1234567)</p>
            <p class="highlight">📌 Demat: Shares held in "Street Name" by IBKR (statement = proof)</p>
          </div>
        </div>

        <!-- Phase B -->
        <div class="process-card blue">
          <div class="phase-label">PHASE B</div>
          <h4>💸 Funding (The LRS Bridge)</h4>
          <div class="step-content">
            <p><strong>1. Access Transfer:</strong> Navigate to "Global Investment" page</p>
            <p><strong>2. Execute LRS Transfer:</strong> From ICICI Bank account</p>
            <div class="limits-box">
              <p><strong>Online Limit:</strong> $25,000 (instant)</p>
              <p><strong>Annual Cap:</strong> $250,000/year</p>
              <p><strong>TCS Trigger:</strong> 20% on >₹10L/year</p>
            </div>
            <p class="note">⚠️ Higher amounts: Contact Relationship Manager</p>
          </div>
        </div>

        <!-- Phase C -->
        <div class="process-card green">
          <div class="phase-label">PHASE C</div>
          <h4>📊 Investment & Trading</h4>
          <div class="step-content">
            <p><strong>Portfolio Options:</strong></p>
            <ul class="options-list">
              <li>Individual stocks on NYSE/NASDAQ</li>
              <li>80+ curated portfolios (from $100)</li>
              <li>Auto-rebalancing available</li>
            </ul>
            <p><strong>Placing Trade:</strong> Click Ask (buy) or Bid (sell) price</p>
            <p class="highlight">✨ Fractional Shares: Buy $10 worth of Nvidia (US markets only)</p>
            <p><strong>Management Fee:</strong> Starting at 0.33% p.a. (managed portfolios)</p>
            <p><strong>Ownership Proof:</strong> Download statements from client portal</p>
          </div>
        </div>

        <!-- Phase D -->
        <div class="process-card orange">
          <div class="phase-label">PHASE D</div>
          <h4>🔄 Redemption & Repatriation</h4>
          <div class="step-content">
            <p><strong>1. Liquidate Assets:</strong> Sell securities during market hours</p>
            <p><strong>2. Cash Ledger:</strong> USD sits in your IBKR account</p>
            <p><strong>3. Withdraw Funds:</strong> Initiate withdrawal to ICICI bank</p>
            <p><strong>Timeline:</strong> 1 to 4 business days</p>
            <div class="costs-box">
              <p><strong>IBKR Fee:</strong> 1 free withdrawal/month, then $10-$25</p>
              <p><strong>Bank Fee:</strong> Inward remittance charges apply</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Critical Nuances -->
    <div class="nuances-section">
      <h3 class="section-header">⚠️ Legal & Taxation Nuances (Critical Watch-Outs)</h3>

      <div class="nuances-grid">
        <!-- Custody -->
        <div class="nuance-card">
          <div class="nuance-icon">🏛️</div>
          <h4>Custody & Protection</h4>
          <p class="nuance-text"><strong>Street Name Holding:</strong> Assets held by IBKR in the US (not in your direct name)</p>
          <p class="nuance-text"><strong>SIPC Protection:</strong> Up to $500,000 (including $250,000 for cash) in case of broker failure</p>
          <p class="nuance-text"><strong>Legal Proof:</strong> IBKR account statements serve as ownership proof</p>
          <div class="info-box">
            💡 Unlike India, US doesn't have separate retail "Demat" system
          </div>
        </div>

        <!-- 60k Rule -->
        <div class="nuance-card danger">
          <div class="nuance-icon">💀</div>
          <h4>The "60k Rule" (Estate Tax)</h4>
          <div class="warning-box">
            <strong>🚨 CRITICAL RISK:</strong> Non-US residents face up to 40% US Estate Tax on death if US-situs assets exceed $60,000
          </div>
          <p class="nuance-text"><strong>Exemption:</strong> $60,000 threshold</p>
          <p class="nuance-text"><strong>Tax Rate:</strong> Up to 40% on excess</p>
          <p class="nuance-text"><strong>Does NOT apply to:</strong></p>
          <ul class="exempt-list">
            <li>✅ GIFT City UDRs</li>
            <li>✅ Feeder Funds</li>
            <li>✅ GIFT City Active Funds</li>
          </ul>
          <p class="strategy-note">📌 Strategy: Keep US direct holdings below $60k or use alternate routes</p>
        </div>

        <!-- Taxation -->
        <div class="nuance-card">
          <div class="nuance-icon">💸</div>
          <h4>Indian Taxation</h4>
          <div class="tax-info">
            <div class="tax-row ltcg">
              <span class="tax-label">LTCG (>24 months):</span>
              <span class="tax-value">20%</span>
              <span class="tax-note">With indexation benefit</span>
            </div>
            <div class="tax-row stcg">
              <span class="tax-label">STCG (<24 months):</span>
              <span class="tax-value">Slab Rate</span>
              <span class="tax-note">Added to income</span>
            </div>
          </div>
          <p class="compare-note">⚠️ Note: Different from domestic equity (12-month LTCG threshold)</p>
        </div>
      </div>
    </div>

    <!-- Comparison Table -->
    <div class="comparison-section">
      <h3 class="section-header">📊 Direct Equity vs Other Routes</h3>
      <div class="comparison-table">
        <div class="compare-row header">
          <div class="compare-col">Feature</div>
          <div class="compare-col">Direct Equity (IBKR)</div>
          <div class="compare-col">GIFT City UDRs</div>
          <div class="compare-col">Feeder Funds</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>Market Access</strong></div>
          <div class="compare-col highlight-green">6 countries, 1000s of stocks</div>
          <div class="compare-col">Top 50 US blue-chips</div>
          <div class="compare-col">Limited indices/themes</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>Fractional Shares</strong></div>
          <div class="compare-col highlight-green">✅ Yes (US markets)</div>
          <div class="compare-col">❌ No</div>
          <div class="compare-col">N/A</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>Custody</strong></div>
          <div class="compare-col">IBKR (Street Name)</div>
          <div class="compare-col highlight-green">CDSL-IFSC Demat</div>
          <div class="compare-col">AMC holds parent fund</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>Estate Tax Risk</strong></div>
          <div class="compare-col highlight-red">⚠️ Yes (>$60k)</div>
          <div class="compare-col highlight-green">✅ No</div>
          <div class="compare-col highlight-green">✅ No</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>LTCG Holding</strong></div>
          <div class="compare-col">24 months</div>
          <div class="compare-col highlight-green">12 months</div>
          <div class="compare-col highlight-green">12 months</div>
        </div>
        <div class="compare-row">
          <div class="compare-col"><strong>Withdrawal Fees</strong></div>
          <div class="compare-col">$10-$25 (after 1st)</div>
          <div class="compare-col highlight-green">Minimal</div>
          <div class="compare-col highlight-green">Minimal</div>
        </div>
      </div>
    </div>

    <!-- Key Takeaways -->
    <div class="takeaways-section">
      <h3 class="section-header">💡 Key Takeaways</h3>
      <div class="takeaways-grid">
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>Widest market access: 6 countries, 1000s of stocks</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>Fractional shares available in US markets</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>80+ managed portfolios with auto-rebalancing</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>US Estate Tax: 40% on assets >$60k (upon death)</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>LTCG threshold: 24 months (vs 12 for domestic)</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>Withdrawal fees: $10-$25 after 1st free per month</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.direct-equity {
  max-width: 1400px;
  margin: 0 auto;
}

.main-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.main-intro {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-left: 3px solid #ef4444;
  border-radius: 6px;
  line-height: 1.4;
}

.main-intro strong {
  color: #991b1b;
}

.section-header {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e5e7eb;
}

/* Platform Section */
.platform-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.platform-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.platform-card h4 {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.architecture-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.arch-item {
  padding: 6px 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 4px;
  font-size: 0.75rem;
  color: #1e3a8a;
}

.arch-item strong {
  color: #1e40af;
  font-weight: 600;
}

.markets-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.market-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 6px;
  font-size: 0.75rem;
}

.market-flag {
  font-size: 1.2rem;
}

.market-info {
  display: flex;
  flex-direction: column;
}

.market-info strong {
  color: #1f2937;
  font-size: 0.75rem;
}

.market-exchange {
  font-size: 0.65rem;
  color: #6b7280;
}

/* Process Flow */
.process-section {
  margin-bottom: 16px;
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.process-card {
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 2px solid;
}

.process-card.purple {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  border-color: #8b5cf6;
}

.process-card.blue {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: #3b82f6;
}

.process-card.green {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #10b981;
}

.process-card.orange {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #f59e0b;
}

.phase-label {
  position: absolute;
  top: -10px;
  left: 10px;
  background: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.7rem;
  border: 2px solid currentColor;
}

.process-card h4 {
  font-size: 0.85rem;
  font-weight: 700;
  margin: 6px 0 8px 0;
  color: #1f2937;
}

.step-content {
  font-size: 0.7rem;
  line-height: 1.3;
}

.step-content p {
  margin: 5px 0;
  color: #374151;
}

.step-content strong {
  color: #1f2937;
  font-weight: 600;
}

.plans-mini {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 4px 0;
}

.plan-mini {
  font-size: 0.7rem;
  padding: 3px 6px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 3px;
}

.limits-box,
.costs-box {
  background: rgba(255, 255, 255, 0.6);
  padding: 6px 8px;
  border-radius: 4px;
  margin: 6px 0;
}

.limits-box p,
.costs-box p {
  margin: 3px 0;
}

.options-list {
  list-style: none;
  padding: 0;
  margin: 4px 0;
}

.options-list li {
  padding: 2px 0;
  font-size: 0.7rem;
  color: #4b5563;
}

.highlight {
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 6px;
  border-radius: 3px;
  font-size: 0.7rem;
  margin-top: 5px !important;
  font-style: italic;
  font-weight: 600;
}

.note {
  font-size: 0.7rem;
  font-style: italic;
  margin-top: 6px !important;
  color: #6b7280;
}

/* Nuances Section */
.nuances-section {
  margin-bottom: 16px;
}

.nuances-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.nuance-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.nuance-card.danger {
  border-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.nuance-icon {
  font-size: 1.5rem;
  margin-bottom: 6px;
}

.nuance-card h4 {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.nuance-text {
  font-size: 0.75rem;
  color: #4b5563;
  line-height: 1.4;
  margin: 4px 0;
}

.nuance-text strong {
  color: #1f2937;
  font-weight: 600;
}

.warning-box {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  padding: 8px;
  border-radius: 6px;
  border-left: 3px solid #ef4444;
  font-size: 0.75rem;
  margin: 8px 0;
  color: #7f1d1d;
  font-weight: 600;
}

.info-box {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  margin-top: 8px;
  color: #1e40af;
}

.exempt-list {
  list-style: none;
  padding: 0;
  margin: 6px 0;
}

.exempt-list li {
  font-size: 0.7rem;
  padding: 2px 0;
  color: #065f46;
}

.strategy-note {
  font-size: 0.7rem;
  margin-top: 8px;
  padding: 6px 8px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 4px;
  color: #78350f;
  font-weight: 600;
}

.tax-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tax-row {
  padding: 8px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tax-row.ltcg {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.tax-row.stcg {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.tax-label {
  font-weight: 600;
  font-size: 0.75rem;
  color: #1f2937;
}

.tax-value {
  font-size: 1rem;
  font-weight: 700;
  color: #065f46;
}

.tax-row.stcg .tax-value {
  color: #92400e;
}

.tax-note {
  font-size: 0.65rem;
  color: #6b7280;
  font-style: italic;
}

.compare-note {
  font-size: 0.7rem;
  margin-top: 8px;
  color: #6b7280;
  font-style: italic;
}

/* Comparison Table */
.comparison-section {
  margin-bottom: 16px;
}

.comparison-table {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.compare-row {
  display: grid;
  grid-template-columns: 1.5fr 2fr 1.5fr 1.5fr;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.75rem;
}

.compare-row.header {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  font-weight: 700;
  color: #1f2937;
  border-bottom: 2px solid #d1d5db;
}

.compare-row:last-child {
  border-bottom: none;
}

.compare-row:hover:not(.header) {
  background: #f9fafb;
}

.compare-col {
  display: flex;
  align-items: center;
  color: #4b5563;
  line-height: 1.4;
}

.compare-col strong {
  color: #1f2937;
}

.compare-col.highlight-green {
  color: #065f46;
  font-weight: 600;
}

.compare-col.highlight-red {
  color: #991b1b;
  font-weight: 600;
}

/* Key Takeaways */
.takeaways-section {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  padding: 16px;
  border-radius: 8px;
  border: 2px solid #fecaca;
}

.takeaways-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.takeaway-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.takeaway-item i {
  font-size: 1rem;
  flex-shrink: 0;
}

.takeaway-item .pi-check-circle {
  color: #10b981;
}

.takeaway-item .pi-exclamation-triangle {
  color: #f59e0b;
}

@media (max-width: 1200px) {
  .process-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .nuances-grid {
    grid-template-columns: 1fr;
  }

  .platform-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .process-grid {
    grid-template-columns: 1fr;
  }

  .markets-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .compare-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .takeaways-grid {
    grid-template-columns: 1fr;
  }
}
</style>
